<template>
  <!-- 结果页整体就是一个视口高的滚动容器（高度/滚动见样式块 .task-evaluation）。
       原先这里只在 paperType == '5' 时给一个 47vh 的固定高度，
       结果页内容一多就只有这一小块在滚、整页滚不动 —— 已移除。 -->
  <!-- paperType=5 时底部有 position:fixed 的答题卡（50vh），它不占文档流却盖住
       本容器下半屏，所以滚动容器要让出这段高度，见样式 .has-answer-sheet。 -->
  <div
    class="task-evaluation"
    :class="{ 'has-answer-sheet': data.paperType == '5' }"
  >
    <div v-if="isStudent && data.answerDisplay != 1" class="answer-tip">
      {{ getAnswerDisplayTip() }}
    </div>

    <div
      v-if="!result.redoList || result.redoList.length < 2"
      class="score-cell"
    >
      <div class="label">总得分</div>
      <div class="score">
        <span :class="`text ${getScoreColor(data.score, data.totalScore)}`">
          {{ data.score }}{{ getScoreStatus() }}
        </span>
        <span class="total">/{{ data.totalScore }}</span>
      </div>
      <div
        v-if="!isStudent"
        class="label"
        style="color: #1890ff; z-index: 99"
        @click="getResource"
      >
        作答详情
      </div>
    </div>

    <div v-else class="row">
      <div class="col-49">
        <div class="score-cell">
          <div class="label" style="flex: 0 0 0.9rem">总分</div>
          <div class="score">
            <span :class="`text ${getScoreColor(data.score, data.totalScore)}`">
              {{ data.score }}{{ getScoreStatus() }}
            </span>
            <span class="total">/{{ data.totalScore }}</span>
          </div>
        </div>
        <div
          v-if="!isStudent"
          class="label"
          style="color: #1890ff; z-index: 99"
          @click="getResource"
        >
          作答详情
        </div>
      </div>
      <div class="col-49">
        <div class="score-cell">
          <div class="label">平均分</div>
          <div class="score">
            <span
              :class="`text ${getScoreColor(
                result.averageScore,
                data.totalScore
              )}`"
            >
              {{ result.averageScore }}
            </span>
            <span class="total">/{{ data.totalScore }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="redo-cell" v-if="result.redoList && result.redoList.length > 1">
      <div class="redo-cell-header row">
        <div class="col-40">完成时间</div>
        <div class="col-30">完成用时</div>
        <div class="col-30">成绩（{{ data.totalScore }}）</div>
      </div>
      <div
        v-for="item in result.redoList"
        :key="item.id"
        :class="`redo-cell-item row ${
          item.id == data.id ? 'redo-bold-row' : ''
        }`"
        @click="toTask(item)"
      >
        <div class="col-40">{{ item.completeTime }}</div>
        <div class="col-30">
          {{ item.duringTime > 0 ? item.duringTime : "<1" }}分钟
        </div>
        <div
          :class="`col-30 redo-cell-score ${getScoreColor(
            item.score,
            data.totalScore
          )}`"
        >
          {{ item.score }}
        </div>
        <van-icon class="arrow-right" name="arrow" v-if="item.id != data.id" />
      </div>
    </div>

    <div class="comment-cell" v-if="result.comment">
      <div class="comment-title">评语</div>
      <div class="comment-content">{{ result.comment }}</div>
    </div>

    <div
      :class="`evaluation-detail ${data.paperType == '5' ? '' : ''}`"
      id="evaluation-detail"
    >
      <div v-html="contentWithLazyLoading" class="data-content"></div>
    </div>
    <AnswerSheet
      :showBtn="false"
      v-if="data.paperType == '5'"
      :answerSheetVH="answerSheetVH"
      :data="data"
    />
  </div>
</template>

<script>
import insert from "../util/insertDOM";
import { mapQuery } from "../util/URLUtils";
import {
  isWritingKnowledgeType,
  isFlowExtraPaperType,
  renderFlowExtras,
} from "../util/flowExtra";
import AnswerSheet from "../components/AnswerSheet";
import { ImagePreview } from "vant";

export default {
  components: {
    AnswerSheet,
  },

  computed: {
    contentWithLazyLoading() {
      return this.ensureImgLazyLoading(this.data?.content);
    },
  },

  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    result: {
      type: Object,
      default: () => ({}),
    },
    isStudent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      answerSheetVH: 50,
      currentPlayingAudio: null,
    };
  },

  beforeDestroy() {
    this.cleanupAudio();
  },

  mounted() {
    this.$nextTick(() => {
      // 作文要求 / 要求附件必须在 renderAnswers 之前注入：填空题分支会把空位
      // ([name=tagId]) 原地换成答案 span，之后再也定位不到该小题；
      // 也要早于 initImagePreview —— 注入的「要求附件」图要一起绑点击放大。
      this.injectFlowExtras();
      this.initAudioControls();
      this.initImagePreview();
      this.renderAnswers();
      this.initTableLayout();
    });
  },

  methods: {
    ensureImgLazyLoading(html) {
      if (!html || typeof html !== "string") return html;

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const imgList = doc.querySelectorAll("img");

        imgList.forEach((img) => {
          if (!img.hasAttribute("loading")) {
            img.setAttribute("loading", "lazy");
          }
        });

        return doc.body ? doc.body.innerHTML : html;
      } catch (e) {
        return html;
      }
    },

    // 处理 v-html 中 table 的宽度和换行，与 PenAndComp.vue 保持一致
    initTableLayout() {
      const container = document.getElementById("evaluation-detail");
      if (!container) return;

      // 处理 table 元素：宽度最大100%，列宽自适应
      const tableList = container.querySelectorAll("table");
      for (let i = 0; i < tableList.length; i++) {
        const tbl = tableList[i];
        tbl.removeAttribute("width");
        // 微信小程序兼容：用 setProperty 设置 !important，确保覆盖 HTML width 属性和 Word 样式
        tbl.style.setProperty("width", "100%", "important");
        tbl.style.setProperty("max-width", "100%", "important");
        // fixed：表格宽度固定为100%，列宽根据首行内容自动分配
        tbl.style.setProperty("table-layout", "fixed", "important");
        tbl.style.setProperty("display", "table", "important");
        // keep-all：保持英语单词完整，仅空格/标点处换行；break-word：单词超宽时才断词
        tbl.style.setProperty("word-break", "keep-all", "important");
        tbl.style.setProperty("overflow-wrap", "break-word", "important");
      }

      // 处理 td/th：移除固定宽度，强制换行
      const tdList = container.querySelectorAll("td, th");
      for (let i = 0; i < tdList.length; i++) {
        const td = tdList[i];
        td.removeAttribute("width");
        td.removeAttribute("nowrap");
        // 清除所有固定宽度（含 pt/px/%），让 table-layout:fixed 自动分配
        td.style.width = "auto";
        td.style.maxWidth = "none";
        // 用 setProperty 设置 !important，确保覆盖 Word 转 HTML 的内联样式
        td.style.setProperty("white-space", "normal", "important");
        // keep-all：保持英语单词完整；break-word：单词超宽时才断词
        td.style.setProperty("word-break", "keep-all", "important");
        td.style.setProperty("word-wrap", "break-word", "important");
        td.style.setProperty("overflow-wrap", "break-word", "important");
        // 清除子元素上的换行限制和固定宽度
        const children = td.querySelectorAll("*");
        for (let j = 0; j < children.length; j++) {
          const child = children[j];
          child.removeAttribute("nowrap");
          child.removeAttribute("width");
          child.style.setProperty("white-space", "normal", "important");
          // keep-all：保持英语单词完整
          child.style.setProperty("word-break", "keep-all", "important");
          child.style.setProperty("word-wrap", "break-word", "important");
          child.style.setProperty("overflow-wrap", "break-word", "important");
          // 清除子元素的固定宽度（pt/px），允许 max-width:100% 限制图片等
          if (
            child.style.width &&
            (child.style.width.includes("pt") ||
              child.style.width.includes("px"))
          ) {
            child.style.width = "";
          }
          // 微信小程序兼容：所有子元素统一限制最大宽度，避免溢出
          child.style.maxWidth = "100%";
        }
      }

      // 移除 col/colgroup 上的宽度限制
      const colList = container.querySelectorAll("col, colgroup");
      for (let i = 0; i < colList.length; i++) {
        colList[i].removeAttribute("width");
        colList[i].style.width = "";
      }

      // 如果第一列的内容只有数字，则第一列设置固定宽度 40px
      const tableListForFirstCol = container.querySelectorAll("table");
      for (let i = 0; i < tableListForFirstCol.length; i++) {
        const tbl = tableListForFirstCol[i];
        const firstRow = tbl.querySelector("tr");
        if (firstRow) {
          const firstCell = firstRow.querySelector("td, th");
          if (firstCell) {
            const text = (firstCell.textContent || "").trim();
            if (text && /^\d+$/.test(text)) {
              firstCell.style.width = "40px";
            }
          }
        }
      }
    },

    // 获取答案显示提示文本
    getAnswerDisplayTip() {
      const tips = {
        3: `当前作业为"仅显示对错"模式，仅显示对错，不显示正确答案`,
        2: `当前作业为"答案关"模式，不显示正确答案`,
        0: `当前作业为"作答达标显示答案"模式，未达标不显示正确答案`,
      };
      return tips[this.data.answerDisplay] || "";
    },

    // 获取得分状态
    getScoreStatus() {
      if (this.data.answerDisplay !== 0) return "";
      return this.data.score == 0
        ? ""
        : this.data.score >= this.data.scoreForAnswer
        ? "（达标）"
        : "（未达标）";
    },

    // 清理音频资源
    cleanupAudio() {
      if (this.currentPlayingAudio) {
        this.currentPlayingAudio.pause();
        this.currentPlayingAudio = null;
      }

      const audioList = document.querySelectorAll("audio");
      audioList.forEach((audio) => {
        const newAudio = audio.cloneNode(true);
        audio.parentNode?.replaceChild(newAudio, audio);
      });
    },

    // 初始化音频控制
    initAudioControls() {
      const audioList = document.querySelectorAll("audio");
      if (!audioList.length) return;

      audioList.forEach((audio) => {
        audio.addEventListener("play", () => this.handleAudioPlay(audio));
        audio.addEventListener("pause", () => this.handleAudioStop(audio));
        audio.addEventListener("ended", () => this.handleAudioStop(audio));
      });

      // 添加听力原文
      if (this.data.listeningText && this.data.showListening === 1) {
        const parent = audioList[0].parentNode;
        const newNode = document.createElement("p");
        newNode.innerHTML = `听力原文：${this.data.listeningText}`;
        parent?.after(newNode);
      }
    },

    // 处理音频播放
    handleAudioPlay(currentAudio) {
      if (
        this.currentPlayingAudio &&
        this.currentPlayingAudio !== currentAudio
      ) {
        this.currentPlayingAudio.pause();
      }

      document.querySelectorAll("audio").forEach((audio) => {
        if (audio !== currentAudio && !audio.paused) {
          audio.pause();
        }
      });

      this.currentPlayingAudio = currentAudio;
    },

    // 处理音频停止
    handleAudioStop(audio) {
      if (this.currentPlayingAudio === audio) {
        this.currentPlayingAudio = null;
      }
    },

    // 初始化图片预览
    initImagePreview() {
      const imgDomList = document.querySelectorAll("#evaluation-detail img");
      imgDomList.forEach((img) => {
        img.addEventListener("click", (e) => {
          ImagePreview({
            images: [e.target.src],
            closeable: true,
          });
        });
      });
    },

    // 渲染答案
    renderAnswers() {
      if (!this.result.subResults) return;

      this.result.subResults.forEach((item) => {
        const domList = Array.from(
          document.querySelectorAll(`[name="${item.tagId}"]`)
        );
        if (!domList.length) return;

        // 选择题：同名的一组 radio/checkbox。只统计真正的选项元素，
        // 避免把 addAnswer 生成的同名展示节点（span）误判成选择题，
        // 否则答题卡里的 textarea / input 会走选择题分支，答案永远填不进去
        const choiceList = domList.filter(
          (el) =>
            el.tagName === "INPUT" &&
            (el.type === "radio" || el.type === "checkbox")
        );

        if (choiceList.length > 1) {
          this.renderMultipleChoice(item, choiceList);
        } else if (this.isWritingAnswer(item)) {
          // 写作(12)：两端都以 userTaskDetail 的 knowledgeType 为准（userTaskResult 不返回
          // 该字段），取不到才回落 tagId 里的 "area"。必须排在 text 判断之前——写作小题的
          // tagId 通常带 text，走错分支就只剩填空渲染（空位被换成一行答案 span），
          // 教师端还会「作文智能分析」永远不出现，学生端则看不到 textarea 答案框。
          this.renderEssay(item, this.pickAnswerTag(domList, "TEXTAREA"));
        } else if (item.tagId.includes("text")) {
          this.renderFillInBlank(item, this.pickAnswerTag(domList, "INPUT"));
        } else if (item.tagId.includes("area")) {
          this.renderEssay(item, this.pickAnswerTag(domList, "TEXTAREA"));
        } else if (domList.length > 1) {
          this.renderMultipleChoice(item, domList);
        }
      });
    },

    // 按标签名精确取目标元素，取不到时退回第一个
    pickAnswerTag(domList, tagName) {
      return domList.find((el) => el.tagName === tagName) || domList[0];
    },

    /* ---------- 作文要求 / 要求附件（来源：flows[].scorePointList） ----------
     * 与 preTask.vue（试卷预览）、PenAndComp.vue（试卷预览 / 学生做作业）共用
     * util/flowExtra.js，四处的口径一致。结果页此前完全没调注入，所以写作小题的
     * 「作文要求 / 要求附件」一直看不到。
     */

    /**
     * 取用于注入的小题列表（flows 与 taskFlowList 都收）。
     * 教师端 data 里是 taskFlowList，学生端是 flows，两份都带上更稳。
     * 按 tagId 去重、taskFlowList 优先（后者覆盖前者）——同一写作小题若两份都带，
     * 不去重会在同一个空位前插出两遍「作文要求」。
     */
    getFlowListForExtras() {
      const map = {};
      const sources = [
        this.data && this.data.flows,
        this.data && this.data.taskFlowList,
      ];
      sources.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((flow) => {
          if (!flow || !flow.tagId) return;
          map[flow.tagId] = flow;
        });
      });
      return Object.keys(map).map((tagId) => map[tagId]);
    },

    /**
     * 按小题注入「作文要求 / 要求附件」（来源 flows[].scorePointList，
     * scorePointList[].simpleTaskSaveDto.content / contentUrl，详见 util/flowExtra.js）。
     * 试卷级门：仅 paperType 为 4 / 5 的试卷（与 preTask、PenAndComp 一致）；
     * 小题级门：仅该小题 knowledgeType 为 12（写作类），在 util 的 getFlowExtraList
     * 里逐小题判断，翻译(16) 等即使给分点里带 simpleTaskSaveDto 也不会被误展示。
     * 容器：正文 #evaluation-detail 优先，答题卡 .answer-sheet-scroll 兜底
     * （paperType=5 时正文内容可能在 data.answerSheet 里、data.content 为空）；
     * 同名空位只插第一处，避免正文与答题卡重复渲染，都找不到时空位兜底挂在正文末尾。
     * ⚠️ 必须在 renderAnswers 之前调用：填空题分支会把空位替换成答案 span。
     */
    injectFlowExtras() {
      if (!isFlowExtraPaperType(this.data && this.data.paperType)) return;
      const flowList = this.getFlowListForExtras();
      if (!flowList.length) return;
      renderFlowExtras(flowList, [
        "evaluation-detail",
        document.querySelector(".answer-sheet-scroll"),
      ]);
      this.bindFlowExtraImagePreview();
    },

    /**
     * 注入的「要求附件」图要能点击放大。
     * 正文 #evaluation-detail 里的图由 initImagePreview 统一绑定（注入先于它执行，
     * 所以会被一起绑上）；这里只补绑落在答题卡等正文之外的图，已绑过的跳过。
     */
    bindFlowExtraImagePreview() {
      const detail = document.getElementById("evaluation-detail");
      const imgList = document.querySelectorAll(".essay-requirement__img");
      imgList.forEach((img) => {
        if (detail && detail.contains(img)) return;
        if (img.dataset && img.dataset.essayPreviewBound === "1") return;
        if (img.dataset) img.dataset.essayPreviewBound = "1";
        img.addEventListener("click", (e) => {
          ImagePreview({
            images: [e.target.src],
            closeable: true,
          });
        });
      });
    },

    /* ---------- 小题类型判定：knowledgeType（来自 /api/exam/userTaskDetail） ----------
     * knowledgeType 挂在**小题(flow)**最外层，只有 userTaskDetail 会返回：
     *   - 教师端 data.taskFlowList（口径与 preTask.vue / 试卷预览一致）
     *   - 兜底 data.flows
     * /api/exam/userTaskResult 的 subResults / aiCorrectList 都没有这个字段，
     * 所以「该小题是不是写作(12)」必须以这里的映射为准，不能只看 result。
     * 层级说明见 util/flowExtra.js 顶部注释。
     */

    /**knowledgeType 归一化成去空白字符串（兼容后端返回 number 12 与 string "12"）**/
    normalizeKnowledgeType(value) {
      if (value === null || value === undefined) return "";
      return String(value).trim();
    },

    /**
     * tagId → knowledgeType 映射。
     * 两个来源都收：先 flows 再 taskFlowList，同一 tagId 以后者（教师端口径）的非空值覆盖；
     * 空值不覆盖已有取值，避免 taskFlowList 没带该字段时把 flows 里的值冲掉。
     * 结果缓存在实例的 _ 前缀属性上（不参与响应式）——结果页 data 只取一次，无需重复构建。
     */
    getKnowledgeTypeMap() {
      if (this._knowledgeTypeMap) return this._knowledgeTypeMap;
      const map = {};
      const sources = [
        this.data && this.data.flows,
        this.data && this.data.taskFlowList,
      ];
      sources.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((flow) => {
          if (!flow || !flow.tagId) return;
          const knowledgeType = this.normalizeKnowledgeType(flow.knowledgeType);
          if (knowledgeType) map[flow.tagId] = knowledgeType;
        });
      });
      this._knowledgeTypeMap = map;
      return map;
    },

    /**按 tagId 取该小题的 knowledgeType，取不到返回 ""**/
    getKnowledgeTypeByTagId(tagId) {
      if (!tagId) return "";
      return this.getKnowledgeTypeMap()[tagId] || "";
    },

    /**
     * 该小题是否写作(12)。**教师端与学生端口径统一**（都以 userTaskDetail 的
     * knowledgeType 为准，判定口径见 util 的 isWritingKnowledgeType）：只要该小题
     * 能取到 knowledgeType，就由它定论——
     *   · 11（翻译）等非写作小题不会再被 tagId 里的 "area" 误判成作文；
     *   · 反过来，tagId 只带 "text"、不带 "area" 的写作小题也能走对分支——
     *     学生端此前就是被这点漏掉的（掉进 renderFillInBlank，空位被换成一行的
     *     答案 span，写作小题看不到 textarea）。
     * 取不到 knowledgeType 时才回落旧的 tagId "area" 字符串猜测。
     */
    isWritingAnswer(item) {
      const tagId = item && item.tagId;
      if (!tagId) return false;
      const knowledgeType = this.getKnowledgeTypeByTagId(tagId);
      if (knowledgeType !== "") {
        return isWritingKnowledgeType(knowledgeType);
      }
      return tagId.includes("area");
    },

    /**
     * 智能分析卡片的题型：12 作文 / 16 翻译。
     * **教师端与学生端口径统一**：优先取 userTaskDetail（data.flows /
     * data.taskFlowList）里该小题的 knowledgeType；不是 12/16 或取不到时，
     * 回落 userTaskResult 里 aiCorrect.type，保证翻译等既有展示不变。
     * 学生端此前不看 knowledgeType，只认 aiCorrect.type——后端该字段不可靠时，
     * 「翻译智能分析」就会退化成通用「智能优化建议」或整张卡都不出现。
     */
    resolveAiAnalysisType(item, aiCorrect) {
      const knowledgeType = this.getKnowledgeTypeByTagId(item && item.tagId);
      const type = knowledgeType === "" ? NaN : Number(knowledgeType);
      if (type === 12 || type === 16) return type;
      return Number(aiCorrect && aiCorrect.type);
    },

    /**
     * 为写作(12)小题补渲染「作文智能分析」卡（**教师端与学生端都显示**）。
     * renderAISuggestion 原先只在填空题分支被调用，作文走的是 renderEssay 分支，
     * 所以翻译(16)有智能分析、作文没有。answerDisplay == 3（仅显示对错）时不展示，
     * 与填空题分支的口径保持一致。
     */
    renderEssayAiSuggestion(item, parent) {
      if (item && item.answerDisplay == 3) return;
      this.renderAISuggestion(item, parent);
    },

    // 判断是否应显示对错标记和颜色（未达标练习模式下不显示）
    shouldShowCorrection(item) {
      if (
        this.data.practiceMode == 1 &&
        this.data.answerDisplay == 0 &&
        this.data.userType == 3 &&
        this.data.score < this.data.scoreForAnswer
      ) {
        return false;
      }
      // answerDisplay==3 仅显示对错，有 standardAnswer 也显示
      return item.answerDisplay == 3 || !!item.standardAnswer;
    },

    // 渲染选择题
    renderMultipleChoice(item, domList) {
      const hasAnswer = item.answer && item.answer.length > 0;
      // 已经显示对错时，radio 选项只能回显，禁止点击选择
      const showCorrection = this.shouldShowCorrection(item);

      domList.forEach((tag, index) => {
        const parent = tag.parentNode;
        if (!parent) return;

        // 选项父级元素改为块级元素，单独占一行，并上下各留 5px 间距
        parent.style.display = "block";
        parent.style.marginTop = "5px";
        parent.style.marginBottom = "5px";
        // 清除父元素内部开头的所有空格
        this.clearLeadingSpaces(parent);

        // 已显示对错时，禁用该 radio（disabled 在微信 webview 中比 pointer-events 更可靠）
        if (showCorrection) {
          tag.disabled = true;
        }

        if (item.answer && item.answer.indexOf(tag.value) > -1) {
          tag.checked = true;

          if (this.shouldShowCorrection(item)) {
            const isCorrect = item.subScore === item.subTotalScore;
            const tagNode = this.createChoiceMarkNode(
              isCorrect ? "√" : "×",
              isCorrect ? "green" : "red"
            );
            parent.appendChild(tagNode);

            // 答错的选项在 × 后追加正确答案（绿色）
            if (!isCorrect && item.standardAnswer) {
              const answerNode = document.createElement("span");
              answerNode.className = "green";
              answerNode.innerHTML = `(正确答案:${item.standardAnswer})`;
              answerNode.style.marginLeft = "4px";
              parent.appendChild(answerNode);
            }
          }
        }

        // 未作答的选择题，在最后一个选项后添加 × 和正确答案
        if (
          !hasAnswer &&
          this.shouldShowCorrection(item) &&
          index === domList.length - 1
        ) {
          const tagNode = this.createChoiceMarkNode("×", "red");
          parent.appendChild(tagNode);

          if (item.standardAnswer) {
            const answerNode = document.createElement("span");
            answerNode.className = "green";
            answerNode.innerHTML = `(正确答案:${item.standardAnswer})`;
            answerNode.style.marginLeft = "4px";
            parent.appendChild(answerNode);
          }
        }

        if (item.remark) {
          const remarkNode = document.createElement("span");
          remarkNode.className = "green";
          remarkNode.innerHTML = `（注释：${item.remark}）`;
          parent.appendChild(remarkNode);
        }
      });
    },

    // 清除元素内部开头的所有空格（含普通空格和 &nbsp;）
    clearLeadingSpaces(el) {
      if (!el) return;
      while (el.firstChild) {
        const node = el.firstChild;
        // 仅处理文本节点
        if (node.nodeType === 3) {
          const trimmed = node.nodeValue.replace(/^[\s\u00a0]+/, "");
          if (trimmed === "") {
            // 纯空白节点，直接移除，继续向后检查
            el.removeChild(node);
            continue;
          }
          // 去掉开头空白后保留其余内容
          node.nodeValue = trimmed;
        }
        break;
      }
    },

    // 创建选择题对错标记节点（保留颜色，与前面的选项水平垂直对齐）
    createChoiceMarkNode(text, className) {
      const node = document.createElement("span");
      node.className = className;
      node.innerHTML = text;
      node.style.fontSize = "inherit";
      node.style.verticalAlign = "baseline";
      node.style.marginLeft = "4px";
      node.style.display = "inline-block";
      return node;
    },

    // 渲染填空题
    renderFillInBlank(item, tag) {
      const parent = tag.parentNode;
      if (!parent) return;

      tag.value = item.answer || "";
      tag.style.width = this.calculateInputWidth(item.answer);

      const showCorrection = this.shouldShowCorrection(item);

      if (showCorrection) {
        tag.className += item.subScore ? " green" : " red";
      }

      if (item.remark) {
        const remarkNode = document.createElement("span");
        remarkNode.className = "green";
        remarkNode.innerHTML = `注释：${item.remark}`;
        parent.insertBefore(remarkNode, tag.nextSibling);
      }

      if (
        showCorrection &&
        item.standardAnswer &&
        item.subScore != item.subTotalScore
      ) {
        const answerNode = document.createElement("span");
        answerNode.className = "green";
        answerNode.innerHTML = `(答案：${item.standardAnswer})`;
        parent.insertBefore(answerNode, tag.nextSibling);
      }

      if (
        showCorrection &&
        (item.answer || item.answerDisplay == 3 || item.standardAnswer)
      ) {
        const resultNode = this.createFillInBlankResult(item);
        parent.replaceChild(resultNode, tag);

        if (item.answerDisplay != 3) {
          this.renderAISuggestion(item, parent);
        }
      }
    },

    // 创建填空题结果节点（使用 span 行内插入，避免 div+translateY 在段落中与 Word 样式叠字）
    createFillInBlankResult(item) {
      const tagNode = document.createElement("span");
      tagNode.className = "fill-blank-result";

      if (item.subScore === item.subTotalScore) {
        tagNode.classList.add("green");
        tagNode.innerHTML = item.answer
          ? `<span class="fill-blank-answer">${item.answer}</span><span class="fill-blank-mark">√</span>`
          : `<span class="fill-blank-mark">√</span>`;
      } else if (item.subScore > 0 && item.subScore < item.subTotalScore) {
        tagNode.classList.add("orange");
        tagNode.innerHTML = item.answer
          ? `<span class="fill-blank-answer">${item.answer}</span><span class="fill-blank-mark">⍻</span>`
          : `<span class="fill-blank-mark">⍻</span>`;
      } else {
        tagNode.classList.add("red");
        tagNode.innerHTML = item.answer
          ? `<span class="fill-blank-answer">${item.answer}</span><span class="fill-blank-mark">×</span>`
          : `<span class="fill-blank-mark">×</span>`;
      }

      return tagNode;
    },

    // 渲染智能优化建议
    renderAISuggestion(item, parent) {
      const aiCorrect = this.result.aiCorrectList?.find(
        (ai) => ai.answerId === item.id
      );

      if (!aiCorrect) return;

      // 作文（type=12）/ 翻译（type=16）走智能分析卡片，其余类型保持原有展示
      // 题型取值：两端都优先 userTaskDetail 的 knowledgeType，见 resolveAiAnalysisType
      const aiType = this.resolveAiAnalysisType(item, aiCorrect);
      if (aiType === 12 || aiType === 16) {
        parent.appendChild(
          aiType === 12
            ? this.createCompositionAnalysis(aiCorrect)
            : this.createTranslationAnalysis(aiCorrect)
        );
        return;
      }

      const aiSuggestionNode = document.createElement("div");
      aiSuggestionNode.className = "ai-suggestion-container";
      aiSuggestionNode.style.cssText = `
        margin-top: 10px;
        padding: 12px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
      `;

      const titleNode = document.createElement("div");
      titleNode.style.cssText = `
        display: flex;
        align-items: center;
        color: #fff;
        font-size: 14px;
        font-weight: bold;
        margin-bottom: 8px;
      `;
      titleNode.innerHTML = `<span style="margin-right: 6px;">🤖</span><span>智能优化建议</span>`;

      const contentNode = document.createElement("div");
      contentNode.style.cssText = `
        background: #fff;
        padding: 10px;
        border-radius: 6px;
        color: #333;
        font-size: 13px;
        line-height: 1.6;
      `;

      if (aiCorrect.content) {
        contentNode.appendChild(
          this.createAISection("回答内容", aiCorrect.content, "#3b82f6")
        );
      }

      const evaluations = [
        {
          message: aiCorrect.evaluateOneMessage,
          score: aiCorrect.evaluateOneScore,
          title: "内容完整性",
        },
        {
          message: aiCorrect.evaluateTwoMessage,
          score: aiCorrect.evaluateTwoScore,
          title: "语言准确性",
        },
        {
          message: aiCorrect.evaluateThreeMessage,
          score: aiCorrect.evaluateThreeScore,
          title: "逻辑连贯性",
        },
      ];

      evaluations.forEach(({ message, score, title }) => {
        if (message) {
          contentNode.appendChild(
            this.createAIEvaluation(title, message, score)
          );
        }
      });

      aiSuggestionNode.appendChild(titleNode);
      aiSuggestionNode.appendChild(contentNode);
      parent.appendChild(aiSuggestionNode);
    },

    // 创建AI内容区块
    createAISection(title, content, borderColor) {
      const section = document.createElement("div");
      section.style.cssText = `
        margin-bottom: 10px;
        padding: 8px;
        background: #f0f9ff;
        border-left: 3px solid ${borderColor};
        border-radius: 4px;
      `;
      section.innerHTML = `
        <div style="color: #1e40af; font-weight: 600; margin-bottom: 4px;">${title}：</div>
        <div style="color: #1e3a8a;">${content}</div>
      `;
      return section;
    },

    // 创建AI评估项
    createAIEvaluation(title, message, score) {
      const item = document.createElement("div");
      const isGood = score !== 0;
      item.style.cssText = `
        margin-bottom: 8px;
        padding: 8px;
        background: ${isGood ? "#f0fdf4" : "#fef2f2"};
        border-left: 3px solid ${isGood ? "#22c55e" : "#ef4444"};
        border-radius: 4px;
      `;
      item.innerHTML = `
        <div style="margin-bottom: 4px;">
          <span style="color: ${
            isGood ? "#16a34a" : "#dc2626"
          }; font-weight: 600;">
            ${title}：
          </span>
        </div>
        <div style="color: #374151;">${message}</div>
      `;
      return item;
    },

    // 取智能分析评分，取不到返回 null（不显示评分区域）
    getAiCorrectScore(item) {
      if (!item) return null;

      const candidates = [
        item.totalScore,
        item.score,
        item.actualScore,
        item.subScore,
      ];

      for (let i = 0; i < candidates.length; i++) {
        const value = candidates[i];
        if (value !== null && value !== undefined && value !== "") {
          return value;
        }
      }

      return null;
    },

    // 翻译（type=16）：答题示例
    getTranslationSample(item) {
      if (!item) return "";

      return (
        item.evaluateOneMessage ||
        item.answerDemo ||
        item.sampleAnswer ||
        item.standardAnswer ||
        item.content ||
        ""
      );
    },

    // 翻译（type=16）：答题分析
    getTranslationAnalysis(item) {
      if (!item) return "";

      return (
        item.evaluateTwoMessage ||
        item.analysisContent ||
        item.optimizationRemark ||
        item.evaluateOneMessage ||
        ""
      );
    },

    // 智能分析卡片外壳（作文 type=12 / 翻译 type=16 共用）
    createAIAnalysisCard(title, score) {
      const card = document.createElement("div");
      card.className = "ai-suggestion-container ai-analysis-card";
      card.style.cssText = `
        margin: 12px 0;
        padding: 16px;
        background: #ffffff;
        border: 1px solid #ebeef5;
        border-radius: 6px;
      `;

      const header = document.createElement("div");
      header.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
      `;

      const titleNode = document.createElement("span");
      titleNode.style.cssText = `
        font-size: 15px;
        font-weight: 700;
        color: #303133;
      `;
      titleNode.innerText = title;
      header.appendChild(titleNode);

      if (score !== null) {
        const scoreNode = document.createElement("span");
        scoreNode.style.cssText = `
          font-size: 14px;
          font-weight: 700;
          flex-shrink: 0;
        `;
        scoreNode.innerHTML = `<span style="color: #2f6fe4;">评分：</span><span style="color: #00baad;">${score}</span>`;
        header.appendChild(scoreNode);
      }

      card.appendChild(header);
      return card;
    },

    // 智能分析行：左侧标签 + 右侧内容
    createAIAnalysisRow(label, content, isExample) {
      const row = document.createElement("div");
      row.style.cssText = `
        display: flex;
        align-items: stretch;
        margin-bottom: 8px;
      `;

      const labelNode = document.createElement("div");
      labelNode.style.cssText = `
        flex: 0 0 auto;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 88px;
        padding: 10px 8px;
        background: #e7e3f8;
        border-radius: 4px 0 0 4px;
        color: #5b3fd6;
        font-size: 13px;
        font-weight: 700;
        text-align: center;
      `;
      labelNode.innerText = label;

      const contentNode = document.createElement("div");
      contentNode.style.cssText = `
        flex: 1;
        min-width: 0;
        padding: 10px 12px;
        background: ${isExample ? "#eef5ff" : "#f4f1fd"};
        border-radius: 0 4px 4px 0;
        color: #333333;
        font-size: 13px;
        line-height: 1.8;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: pre-wrap;
      `;
      contentNode.innerText = content;

      row.appendChild(labelNode);
      row.appendChild(contentNode);
      return row;
    },

    // 作文（type=12）：作文智能分析（内容 / 语言 / 组织结构）
    createCompositionAnalysis(aiCorrect) {
      const card = this.createAIAnalysisCard(
        "作文智能分析：",
        this.getAiCorrectScore(aiCorrect)
      );

      const dimensions = [
        { label: "内容：", message: aiCorrect.evaluateOneMessage },
        { label: "语言：", message: aiCorrect.evaluateTwoMessage },
        { label: "组织结构：", message: aiCorrect.evaluateThreeMessage },
      ];

      dimensions.forEach(({ label, message }) => {
        if (message) {
          card.appendChild(this.createAIAnalysisRow(label, message, false));
        }
      });

      return card;
    },

    // 翻译（type=16）：翻译智能分析（答题示例 / 答题分析）
    createTranslationAnalysis(aiCorrect) {
      const card = this.createAIAnalysisCard(
        "翻译智能分析：",
        this.getAiCorrectScore(aiCorrect)
      );

      const sample = this.getTranslationSample(aiCorrect);
      if (sample) {
        card.appendChild(this.createAIAnalysisRow("答题示例：", sample, true));
      }

      const analysis = this.getTranslationAnalysis(aiCorrect);
      if (analysis) {
        card.appendChild(
          this.createAIAnalysisRow("答题分析：", analysis, false)
        );
      }

      return card;
    },

    /**
     * 写作(12)小题的答案框统一成只读 textarea。
     * 正文里的空位是单行 <input>，整篇作文只能显示一行；这里原地换成 textarea，
     * 保留 name / id（[name=tagId] 定位、作文要求注入、批改数据匹配都靠它），
     * 原 class 一并保留，再叠加 .essay-answer-textarea 控制满宽与固定行高。
     * 高度固定 10 行（rows="10"，与作答页 PenAndComp 的写作框一致）：内容短时留白可控，
     * 内容超出 10 行时由 .essay-answer-textarea 的 overflow-y:auto 出内部上下滚动条，
     * 不再把结果页越撑越长——所以这里【不要】再按内容撑高（撑高后 scrollHeight 恒等于
     * clientHeight，反而永远不出现滚动条）。
     * ⚠️ 不复用 PenAndComp 的 .writing-answer-textarea：两个 class 都是全局样式
     *（两个样式块都没 scoped），复用会互相覆盖；这里单独维护一份，口径保持一致。
     */
    ensureEssayAnswerTextarea(tag) {
      if (!tag || !tag.parentNode) return tag;

      if (tag.tagName === "TEXTAREA") {
        tag.readOnly = true;
        // 答题卡自带的 textarea 行数不统一，这里按样式约定的 10 行对齐
        tag.setAttribute("rows", "10");
        if (!tag.classList || !tag.classList.contains("essay-answer-textarea")) {
          tag.className = `${tag.className ? `${tag.className} ` : ""}essay-answer-textarea`;
        }
        return tag;
      }

      const textarea = document.createElement("textarea");
      if (tag.name) textarea.name = tag.name;
      if (tag.id) textarea.id = tag.id;
      textarea.className = `${
        tag.className ? `${tag.className} ` : ""
      }essay-answer-textarea`;
      textarea.readOnly = true;
      textarea.setAttribute("rows", "10");
      textarea.value = tag.value || "";
      tag.parentNode.replaceChild(textarea, tag);
      return textarea;
    },

    // 渲染作文题
    renderEssay(item, tag) {
      if (!tag) return;
      let parent = tag.parentNode;
      if (!parent) return;

      // 写作小题的答案一律用 textarea 展示（正文里的单行 input 会被换成 textarea），
      // 换完 parent 仍是同一个父元素，无需重新取
      tag = this.ensureEssayAnswerTextarea(tag);

      // 先把学生答案回显到 textarea：批改数据缺失时若直接 return，
      // 答题卡里就只剩一个空白 textarea，看不到已作答内容。
      // 高度由 CSS 固定为 10 行，超出部分内部滚动，这里不再按内容撑高
      tag.value = item.answer || "";

      // 无批改数据（correctData / sentenceComments 缺失）时降级：只读回显答案
      if (!item.correctData || !item.correctData.sentenceComments) {
        // 两端一致：没有作文批改数据也要把「作文智能分析」卡补上
        this.renderEssayAiSuggestion(item, parent);
        return;
      }

      const answer = this.processEssayAnswer(item);
      const newNode = this.createEssayNode(item, answer);

      if (this.shouldShowCorrection(item)) {
        // 仅显示对错 / 有标准答案：答案本身要留在 textarea 里——原写法是用 √/× 原地
        // 替换空位，整篇作文直接消失；改为标记追加在答案框之后（紧邻答案，不跨段）
        parent.insertBefore(
          this.createMarkNode(
            item.subScore === item.subTotalScore ? "√" : "×",
            item.subScore === item.subTotalScore ? "green" : "red"
          ),
          tag.nextSibling
        );
      } else {
        // 有逐句批改数据：答案以「作文批改分析」（标注版）展示，原始 textarea 隐藏避免重复
        tag.className += " dn";
        parent.append(newNode);
      }

      // 作文(12)的「作文智能分析」卡（两端一致）——此前只在填空题分支渲染，作文一直看不到
      this.renderEssayAiSuggestion(item, parent);
    },

    // 处理作文答案
    processEssayAnswer(item) {
      let answer = item.answer;
      const sentenceComments = item.correctData.sentenceComments;

      sentenceComments.forEach((comment) => {
        let sentence = comment.sentence.sentence;
        const sentenceArr = sentence.split(" ");
        const deletedErrorArr = sentenceArr.slice();

        comment.suggestions.forEach((sug) => {
          const [error0, error1] = sug.errorPosition;
          const errorArr = sentenceArr.slice(error0, error1 + 1);
          deletedErrorArr.splice(error0, error1 - error0 + 1);

          const hintText = this.createHintText(errorArr, sug);
          deletedErrorArr.splice(error0, 0, hintText);

          if (error1 - error0 > 0) {
            for (let k = 0; k < error1 - error0; k++) {
              deletedErrorArr.splice(error0, 0, "");
            }
          }

          const newSentence = deletedErrorArr.join(" ");
          answer = answer.replace(sentence, newSentence);
          sentence = newSentence;
        });
      });

      return answer;
    },

    // 创建提示文本
    createHintText(errorArr, sug) {
      if (sug.origin) {
        return `${errorArr
          .join(" ")
          .replace(
            sug.origin,
            `<span class="red">${sug.origin}`
          )}</span><span style="font-weight: bold">（${sug.message}）</span>`;
      }

      if (errorArr.length == 2) {
        return `${errorArr[0]}<span style="font-weight: bold">（${sug.message}）</span>${errorArr[1]}`;
      }

      return `<span class="red">${errorArr.join(
        " "
      )}</span><span style="font-weight: bold">（${sug.message}）</span>`;
    },

    // 创建作文节点
    createEssayNode(item, answer) {
      const newNode = document.createElement("div");
      newNode.className = "evaluation-area";

      const content = document.createElement("div");
      content.className = "area-content";
      content.innerHTML = answer;

      const holder2 = document.createElement("div");
      holder2.className = "holder2";
      content.append(holder2);

      const scoreCat = item.correctData.scoreCat;
      content.append(
        insert.scoreCell({
          score: [item.subScore],
          total: [item.subTotalScore],
          count: 1,
          label: ["单题总分"],
        })
      );

      content.append(
        insert.scoreCell({
          score: [scoreCat.content.score, scoreCat.sentences.score],
          total: [100, 100],
          count: 2,
          label: [scoreCat.content.name, scoreCat.sentences.name],
        })
      );

      content.append(
        insert.scoreCell({
          score: [scoreCat.structure.score, scoreCat.words.score],
          total: [100, 100],
          count: 2,
          label: [scoreCat.structure.name, scoreCat.words.name],
        })
      );

      content.append(
        insert.evaluationCell({
          comment: item.correctData.comment,
        })
      );

      const etitle = document.createElement("div");
      etitle.className = "etitle";
      etitle.innerHTML = "作文批改分析：";

      newNode.append(etitle, content);
      return newNode;
    },

    // 创建标记节点
    createMarkNode(text, className) {
      const node = document.createElement("span");
      node.className = className;
      node.innerHTML = text;
      node.style.fontSize = "20px";
      node.style.transform = "translateY(5px)";
      node.style.display = "inline-block";
      return node;
    },

    // 计算输入框宽度
    calculateInputWidth(answer) {
      if (!answer) return "100px";
      const answerList = answer.split(" ");
      return answerList.length > 2 ? `${answerList.length * 50}px` : "100px";
    },

    getScoreColor(score, total) {
      return insert.getScoreColor(score, total);
    },

    getResource() {
      let duringTime = 0;
      if (this.result.redoList?.length > 0) {
        const current = this.result.redoList.find(
          (r) => r.id === this.result.id
        );
        duringTime = current?.duringTime || 0;
      }

      wx.miniProgram.redirectTo({
        url: `/pages/student-task-detail/index?userTaskId=${this.result.id}&duringTime=${duringTime}`,
      });
    },

    toTask(item) {
      if (item.id != this.data.id) {
        const query = {
          ...mapQuery(),
          id: item.id,
        };
        this.$router.replace({ path: "/pen", query });
      }
    },
  },
};
</script>

<style lang="less">
.task-evaluation {
  padding-top: 0.4rem;
  box-sizing: border-box;
  /* 整个结果页 = 视口高的滚动容器：内容多了出上下滚动条。
     原先只有 paperType == '5' 时在模板上绑 `height: 47vh; overflow-y: auto`，
     结果页内容一多就只有批改详情那一小块在滚、整页滚不动 —— 现统一为整页滚动。
     不依赖 html/body 的隐式滚动：App.vue 里有 `body { overflow: hidden; position: absolute }`。 */
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  // 禁止 iOS 橡皮筋效果，防止滚动到顶/底时露出空白（与 .homework-page 一致）
  overscroll-behavior: none;

  /* paperType=5（模板上加 .has-answer-sheet）：底部答题卡是
     `.answer-sheet-wrapper { position: fixed; bottom: 0; height: 50vh }`（AnswerSheet.vue），
     它固定盖住本容器下半屏且不占文档流 —— 本容器高度按 100vh 算，
     滚到 scrollTop 最大时正文最后 50vh 刚好落在答题卡后面，永远滑不出来
     （症状：正文/图片上下看不全、怎么滑都差一截）。
     padding-bottom 会计入可滚动区域，让出等高的一段后，滚到底时正文底边正好
     停在答题卡上沿。数值与 data.answerSheetVH 保持一致。 */
  &.has-answer-sheet {
    padding-bottom: 50vh;
  }

  .evaluation-detail {
    font-family: "Times New Roman";
    font-size: 12pt;
    line-height: 1.6em;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    .data-content {
      white-space: normal;
      word-break: break-word;
      width: 100%;
      overflow-x: auto;
      box-sizing: border-box;
      -webkit-overflow-scrolling: touch;
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed !important;
        display: table !important;
        word-break: keep-all !important;
        overflow-wrap: break-word !important;
        margin: 0 !important;
      }
      table tr {
        height: auto !important;
        td,
        th {
          height: auto !important;
          word-break: keep-all !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          p,
          span,
          div {
            word-break: keep-all !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            white-space: normal !important;
          }
        }
      }
    }
  }

  /* v-html / Word 导出：段内 .green 曾用 div+inline-block+translateY，易与前后文水平重叠 */
  .data-content {
    .fill-blank-result {
      display: inline;
      vertical-align: baseline;
      white-space: normal;
      font-size: 1.15em;
      line-height: inherit;
    }

    .fill-blank-answer {
      text-decoration: underline;
      text-underline-offset: 2px;
      padding: 0 2px;
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }

    .fill-blank-mark {
      display: inline;
      vertical-align: baseline;
      margin-left: 1px;
    }

    div.green {
      display: inline !important;
      transform: none !important;
      vertical-align: baseline;
      margin: 0;
      padding: 0;
      white-space: normal;
    }

    div.green > span[style*="text-decoration"],
    div.green > span[style*="underline"] {
      padding: 0 3px !important;
      vertical-align: baseline;
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }
  }

  input[type="radio"],
  input[type="text"],
  textarea {
    pointer-events: none;
  }

  /* 禁用后的 radio 保持正常外观，不变灰，保证已选中状态清晰回显 */
  input[type="radio"]:disabled {
    opacity: 1;
    cursor: default;
  }
}

/* iOS 动态地址栏：dvh 按实际可视高度算，比 100vh 少被地址栏吃掉一截；
   不支持 dvh 的机型 @supports 判假，自动退回上面的 100vh。 */
@supports (height: 100dvh) {
  .task-evaluation {
    height: 100dvh;
  }
}

.answer-tip {
  padding: 10px 20px 0;
  color: red;
  font-size: 12px;
}

/* 「作文要求 / 要求附件」块：由 util/flowExtra.js 按小题注入到正文/答题卡内部，
   节点由 JS 生成、不带 scoped 的 data-v，本样式块未 scoped 可直接命中。
   类名与 preTask.vue、PenAndComp.vue 及后台 Preview / StudentTask 一致，三端口径统一；
   配色沿用同一套（底 #fdf6ec / 边框 #faecd8 / 正文 #303133 / 标签 #e6a23c / 附件图描边 #ebeef5），
   字号用本页 rem 750 尺度。 */
.essay-requirement {
  margin: 0.2rem 0;
  padding: 0.2rem 0.24rem;
  background-color: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 0.12rem;
  box-sizing: border-box;

  .essay-requirement__title,
  .essay-requirement__subtitle {
    font-size: 0.34rem;
    font-weight: bold;
    color: #e6a23c;
    line-height: 1.5;
    margin-bottom: 0.12rem;
  }

  .essay-requirement__content {
    font-size: 0.3rem;
    color: #303133;
    line-height: 1.6;
    word-break: break-word;
    white-space: normal;

    img {
      max-width: 100%;
      height: auto;
    }
  }

  .essay-requirement__img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 0.08rem;
    border: 1px solid #ebeef5;
  }
}

/* 写作(12)小题的答案框：节点由 JS 生成/改写，不带 scoped 的 data-v，本样式块未 scoped 可直接命中。
   高度固定 10 行：height:auto !important 让浏览器按 rows="10" 算高（并压掉可能残留的行内 height），
   内容超出 10 行时由 overflow-y:auto 出内部上下滚动条，结果页不会被长作文越撑越长。
   与 PenAndComp 的 .writing-answer-textarea（学生作答）刻意分开两个 class——
   两个样式块都是全局的，复用会互相覆盖。字号/配色沿用同一套 rem 750 尺度。 */
.essay-answer-textarea {
  display: block;
  width: 100% !important;
  height: auto !important;
  box-sizing: border-box;
  margin: 0.12rem 0;
  padding: 0.12rem 0.16rem;
  border: 1px solid #dcdfe6;
  border-radius: 0.08rem;
  background-color: #fff;
  font-size: 0.3rem;
  line-height: 1.6;
  color: #303133;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  resize: none;
  vertical-align: top;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 只读答案框要能上下滚动：.task-evaluation 里的 `textarea { pointer-events: none }`
   会给所有 textarea 关掉指针事件，滚动也随之失效（滚动条看得见、拖不动）。
   这里用更高优先级的选择器放开；「不可编辑」由 readOnly 属性保证，
   放开 pointer-events 不会让它变成可输入（也不会弹软键盘）。 */
.task-evaluation textarea.essay-answer-textarea {
  pointer-events: auto;
}

p {
  margin: 10px 0 !important;
}
</style>
